import React, { useMemo } from "react";
import { Box, Container, Typography, Chip, Paper, Grid, TextField, Button } from "@mui/material";
import { GridRenderCellParams, GridRowParams, GridColDef } from "@mui/x-data-grid-pro";
import { InventoryGroupRule } from "types/inventoryGroupRule";
import { dateColDef } from "components/DataGrid/columnDefs/dateColDef";
import { RevivnApiForm } from "components/Form/RevivnApiForm";
import { Field } from "formik";
import { RevivnDatabaseDataGrid } from "components/DataGrid/RevivnDatabaseDataGrid";

const InventoryGroupRulesPage: React.FC = () => {
  const columns = useMemo<GridColDef[]>(
    () => [
      { field: "id", headerName: "ID", width: 70 },
      { field: "name", headerName: "Name", width: 150 },
      {
        field: "group",
        headerName: "Group",
        width: 120,
        renderCell: (params: GridRenderCellParams<InventoryGroupRule>) => (
          <Chip label={params.row.group} color="primary" />
        ),
      },
      { field: "priority", headerName: "Priority", width: 100 },
      {
        field: "conditions",
        headerName: "Conditions",
        width: 300,
        renderCell: (params: GridRenderCellParams<InventoryGroupRule>) => (
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, maxWidth: 280 }}>
            <Chip
              size="small"
              label={`${Object.keys(params.row.conditions).length} conditions`}
              color="primary"
              variant="outlined"
            />
          </Box>
        ),
      },
      {
        field: "createdAt",
        headerName: "Created At",
        width: 180,
        ...dateColDef,
      },
      {
        field: "updatedAt",
        headerName: "Updated At",
        width: 180,
        ...dateColDef,
      },
    ],
    [],
  );

  const getDetailPanelContent = (params: GridRowParams<InventoryGroupRule>) => {
    return (
      <Box sx={{ p: 2, my: 1, bgcolor: "background.default" }}>
        <Typography variant="h6" gutterBottom>
          {params.row.name} Conditions
        </Typography>
        <Grid container spacing={2}>
          {Object.entries(params.row.conditions).map(([key, value]) => (
            <ConditionItem key={key} name={key} value={value} />
          ))}
        </Grid>
      </Box>
    );
  };

  return (
    <Container sx={{ mt: 4 }}>
      <Typography variant="h4" component="h1">
        Inventory Group Rules
      </Typography>

      <Paper sx={{ p: 2, my: 3 }}>
        <Typography variant="h6" gutterBottom>
          Test Inventory Group Rules
        </Typography>
        <RevivnApiForm<{ inventoryId: string }, { inventoryGroup: string }>
          action="inventory_group_rules/query"
          method="POST"
          initialValues={{ inventoryId: "" }}
          onSuccess={({ data: { inventoryGroup } }: { data: { inventoryGroup: any } }) => {
            alert(`Matched Inventory Group: ${inventoryGroup || "No group matched"}`);
          }}
          onError={(error: { message: any }) => {
            alert(`Error: ${error.message || "Failed to test rules"}`);
          }}
        >
          <Field
            name="inventoryId"
            as={TextField}
            size="small"
            label="Inventory ID"
            placeholder="Enter inventory ID to test"
            required
            fullWidth
            sx={{ mb: 2 }}
          />
          <Button type="submit" variant="contained" fullWidth>
            Test Rules
          </Button>
        </RevivnApiForm>
      </Paper>

      <RevivnDatabaseDataGrid
        url="inventory_group_rules"
        columns={columns}
        autoHeight
        syncStateWithSearchParams
        getDetailPanelContent={getDetailPanelContent}
        getDetailPanelHeight={() => "auto"}
      />
    </Container>
  );
};

const ConditionItem = ({ name, value }: { name: string; value: any }) => {
  let displayValue = "";
  let chipColor: "primary" | "secondary" | "success" | "error" | "info" | "warning" | undefined = "primary";

  if (name === "cpu" && typeof value === "object" && "regex" in value) {
    displayValue = `CPU: ${value.regex}`;
    chipColor = "info";
  } else if (name === "year" && typeof value === "object" && "min" in value) {
    const yearRange = value.max ? `${value.min}-${value.max}` : `>${value.min}`;
    displayValue = `Year: ${yearRange}`;
    chipColor = "success";
  } else if (name === "manufacturer" && typeof value === "object" && "regex" in value) {
    displayValue = `Manufacturer regex: ${value.regex}`;
    chipColor = "secondary";
  } else if (name === "deviceType") {
    displayValue = `Device Type: ${value}`;
    chipColor = "warning";
  } else if (name === "manufacturer" && typeof value === "string") {
    displayValue = `Manufacturer: ${value}`;
    chipColor = "secondary";
  } else if (name === "year" && typeof value === "number") {
    displayValue = `Year: ${value}`;
    chipColor = "success";
  } else {
    displayValue = `${name}: ${JSON.stringify(value)}`;
  }

  return (
    <Grid item>
      <Chip label={displayValue} color={chipColor} sx={{ mb: 1 }} />
    </Grid>
  );
};

export default InventoryGroupRulesPage;
